/**
 * Public legal pages for A2P / Twilio review. No auth.
 * /legal/privacy  /legal/terms  /legal/sms-opt-in  /legal/data-deletion
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const PAGES = {
  '/legal/privacy': { title: 'Privacy Policy', updated: 'September 1, 2026' },
  '/legal/terms': { title: 'Terms of Service', updated: 'September 1, 2026' },
  '/legal/sms-opt-in': { title: 'SMS opt-in', updated: 'September 1, 2026' },
  '/legal/data-deletion': { title: 'Data deletion instructions', updated: 'September 30, 2026' },
};

const wrap = {
  minHeight: '100vh',
  background: '#f7f5f0',
  color: '#1a3a2a',
  fontFamily: 'Georgia, "Times New Roman", serif',
};

const header = {
  background: '#1a3a2a',
  color: '#e8f5ee',
  padding: '1.25rem 1.5rem',
};

const nav = { display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.9rem', marginTop: '0.5rem' };

const link = { color: '#e8f5ee', textDecoration: 'underline', fontWeight: 600 };

const main = { maxWidth: 760, margin: '0 auto', padding: '2rem 1.25rem 4rem', lineHeight: 1.55, fontSize: '1.02rem' };

const h2 = { marginTop: '2rem', fontSize: '1.25rem' };

const box = {
  background: '#fff',
  border: '1px solid #c9c4b8',
  borderRadius: 6,
  padding: '1.25rem 1.4rem',
  margin: '1rem 0',
};

const formLine = { borderBottom: '1px solid #1a3a2a', minHeight: '1.6rem', margin: '0.75rem 0 1rem' };

const footer = {
  background: '#1a3a2a',
  color: '#7ecba1',
  textAlign: 'center',
  padding: '2rem',
  fontSize: '0.85rem',
};

function LegalChrome({ children }) {
  const { pathname } = useLocation();
  const page = PAGES[pathname] || { title: 'Legal', updated: '' };
  return (
    <div style={wrap}>
      <header style={header}>
        <div style={{ fontSize: '0.8rem', letterSpacing: '0.04em' }}>CR Hood Solutions, LLC</div>
        <h1 style={{ margin: '0.35rem 0 0', fontSize: '1.75rem' }}>{page.title}</h1>
        {page.updated ? <div style={{ opacity: 0.8, marginTop: 4 }}>Last updated {page.updated}</div> : null}
        <nav style={nav}>
          <Link to="/" style={link}>Home</Link>
          <Link to="/legal/privacy" style={link}>Privacy</Link>
          <Link to="/legal/terms" style={link}>Terms</Link>
          <Link to="/legal/sms-opt-in" style={link}>SMS opt-in</Link>
          <Link to="/legal/data-deletion" style={link}>Data deletion</Link>
        </nav>
      </header>
      <main style={main}>{children}</main>
      <footer style={footer}>
        © {new Date().getFullYear()} CR Hood Solutions, LLC · Bullard, TX
        {' · '}
        <Link to="/legal/privacy" style={link}>Privacy Policy</Link>
        {' · '}
        <Link to="/legal/terms" style={link}>Terms</Link>
        {' · '}
        <Link to="/legal/sms-opt-in" style={link}>SMS opt-in</Link>
        {' · '}
        <Link to="/legal/data-deletion" style={link}>Data deletion</Link>
      </footer>
    </div>
  );
}

function PrivacyBody() {
  return (
    <>
      <p>CR Hood Solutions, LLC (“we,” “our,” or “us”) operates the Business Manager platform. This Privacy Policy describes how we collect, use, store, protect, and share personal information. It applies to tenant administrators, staff, and end customers who interact with tenant storefronts or services.</p>

      <h2 style={h2}>Information we collect</h2>
      <p>Account information: name, email address, phone number (including a mobile number when provided for account texts), and mailing address. Payment card details are collected by Stripe and are never stored on our servers. We also collect order information, messages you send through the platform, and access logs.</p>
      <p>Plaid and Stripe collect banking and card data under their own privacy policies. We receive account names, transaction data, balances, and payment confirmations only as needed to operate the tenant back office.</p>

      <h2 style={h2}>How we use information</h2>
      <p>We use information to provide the service, authenticate users, manage finances, send order confirmations and transactional text messages, and keep the platform secure. We do not sell personal information. We do not use personal information for advertising.</p>

      <h2 style={h2}>How we share information</h2>
      <p>We share information with the tenant business you deal with, and with service providers that operate the platform: Stripe, Plaid, Twilio (transactional SMS), SendGrid (transactional email), Railway, Vercel, and Squarespace (domain registration and DNS). We may disclose information when required by law or to protect rights and safety.</p>
      <p><strong>We do not sell, rent, or share mobile phone numbers with third parties for their marketing or promotional use.</strong> A mobile number is used only to deliver the requested account message and to operate the messaging program, including opt-out handling. The above excludes text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>

      <h2 style={h2}>Text messages (SMS)</h2>
      <div style={box}>
        <p>CR Hood Solutions, LLC and tenant businesses that use Business Manager may send transactional text messages to a mobile number the customer provided to that business. Messages may include a link to view an invoice, review and accept an estimate, or open a Pay now checkout page. Messages identify the tenant shop by name. We do not send marketing, promotions, or job-progress updates as part of this program.</p>
        <p>Message frequency varies and is event-based, typically 1 to 4 messages per invoice or estimate cycle. <strong>Message and data rates may apply.</strong></p>
        <p>Reply STOP to cancel. Reply HELP for help. Email robin@crhoodsolutions.com for support. Consent is not a condition of purchase.</p>
      </div>

      <h2 style={h2}>Contact</h2>
      <p>CR Hood Solutions, LLC · Robin Hood · 3950 County Road 3802, Bullard, Texas 75757 · robin@crhoodsolutions.com</p>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <p>These Terms of Service govern use of Business Manager and related sites operated by CR Hood Solutions, LLC, including busmgr.com. By using the platform or providing a mobile number for account texts, you agree to these terms and to the <Link to="/legal/privacy">Privacy Policy</Link>.</p>

      <h2 style={h2}>The service</h2>
      <p>Tenant businesses use Business Manager to keep books, manage customers, and send invoices and estimates. End customers of a tenant may receive account messages (email or text) from that tenant through the platform.</p>

      <h2 style={h2}>SMS terms</h2>
      <div style={box}>
        <p>By providing a mobile number to a tenant business that uses Business Manager, you agree that CR Hood Solutions, LLC (on behalf of that business) may send transactional text messages to that number, including invoice, estimate, and Pay now links. Messages identify the shop by name. This program does not send marketing or promotional texts.</p>
        <p>Consent is not a condition of purchase. Message frequency is event-based (typically 1 to 4 messages per invoice or estimate cycle). Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</p>
        <p>Reply STOP to opt out. Reply HELP for help. Support: robin@crhoodsolutions.com</p>
      </div>

      <h2 style={h2}>Acceptable use</h2>
      <p>You will not use the service to send spam, unsolicited marketing texts, or messages to numbers you do not have permission to contact.</p>

      <h2 style={h2}>Contact</h2>
      <p>CR Hood Solutions, LLC · 3950 County Road 3802, Bullard, Texas 75757 · robin@crhoodsolutions.com</p>
    </>
  );
}

function DataDeletionBody() {
  return (
    <>
      <p>CR Hood Solutions, LLC (“we,” “our,” or “us”) operates Business Manager. These instructions explain how to ask us to delete data we received from Meta (Facebook and Instagram) when a Facebook Page or a linked Instagram professional account is connected to Business Manager.</p>

      <h2 style={h2}>How to request deletion</h2>
      <div style={box}>
        <p>Email <a href="mailto:robin@crhoodsolutions.com?subject=DATA%20DELETION%20REQUEST">robin@crhoodsolutions.com</a> with the subject line <strong>DATA DELETION REQUEST</strong>.</p>
        <p>Include your name, the Facebook Page name, the Instagram username if one was connected, and the business name used in Business Manager. If you can, also include the email address on the Facebook account that approved the connection.</p>
      </div>
      <p>A staff user of that business can also disconnect the Page or Instagram account in Back Office under Social Media → Manage Accounts. Disconnecting removes the stored connection and access token for that account.</p>

      <h2 style={h2}>What we delete</h2>
      <p>After a complete request, we delete the data we stored from Meta for that connection:</p>
      <ul>
        <li>The Facebook Page id, name, and picture address</li>
        <li>The Instagram professional account id, username, and picture address</li>
        <li>The access token used to publish for that Page</li>
        <li>Our record that those accounts were connected to the business</li>
      </ul>
      <p>We complete that deletion within 30 days and reply to the same email when it is done.</p>

      <h2 style={h2}>What stays on Facebook and Instagram</h2>
      <p>Posts already published to a Facebook Page or Instagram account remain on those services. Remove those posts in Facebook or Instagram. This request deletes our copy of the connection, not the live post on Meta’s apps.</p>
      <p>Accounting records, orders, and other business records that did not come from Meta are kept under our <Link to="/legal/privacy">Privacy Policy</Link>.</p>

      <h2 style={h2}>Contact</h2>
      <p>CR Hood Solutions, LLC · Robin Hood · 3950 County Road 3802, Bullard, Texas 75757 · robin@crhoodsolutions.com</p>
    </>
  );
}

function SmsOptInBody() {
  return (
    <>
      <p>This is the hosted opt-in record for Business Manager account texts. Tenant staff use the verbal script or the paper form. There is no public web form and no keyword (START) opt-in.</p>

      <h2 style={h2}>How customers consent</h2>
      <div style={box}>
        <p>Customers of tenant businesses that use Business Manager (CR Hood Solutions, LLC) agree to receive SMS text messages when they say yes to the verbal script or sign the paper form. Staff then enter that mobile number on the customer record. Giving a number for general service is not enough; they must agree to SMS.</p>
        <p>A text is sent only after that number is on file and only for a transactional account message from that shop: a link to view an invoice, review and accept an estimate, or open a Pay now checkout page. Messages identify the shop by name. We do not buy lists, scrape numbers, or send marketing or promotional texts.</p>
        <p>Message frequency is event-based, typically 1 to 4 messages per invoice or estimate cycle. Message and data rates may apply. Reply STOP to opt out or HELP for help.</p>
      </div>

      <h2 style={h2}>Verbal script</h2>
      <div style={box}>
        <p><strong>Staff:</strong> “Do you agree to receive SMS text messages from this shop through Business Manager? We would text a link when we send an invoice, an estimate to review, or a Pay now page. That is usually one to four messages per invoice or estimate. Message and data rates may apply. Reply HELP for help or STOP to opt out. Terms are at busmgr.com/legal/terms. Privacy is at busmgr.com/legal/privacy. You can say no and still get service. Do you agree to receive these text messages — yes or no?”</p>
        <p><strong>Customer:</strong> “Yes.”</p>
        <p><strong>Staff:</strong> “Thank you. We will use the mobile number you gave us. You can reply STOP at any time.”</p>
      </div>

      <h2 style={h2}>Paper SMS opt-in form</h2>
      <p>Print this form. Keep the signed copy. Campaign review photo: <a href="/legal/sms-opt-in-form.png">busmgr.com/legal/sms-opt-in-form.png</a> (static host copy: <a href="/legal/sms-opt-in-form.html">sms-opt-in-form.html</a>).</p>
      <p><img src="/legal/sms-opt-in-form.png" alt="Paper SMS opt-in form" style={{ width: '100%', height: 'auto', border: '1px solid #c9c4b8' }} /></p>
      <div style={{ ...box, borderWidth: 2 }}>
        <p style={{ fontWeight: 700, marginTop: 0 }}>Business Manager account texts — opt-in</p>
        <p>CR Hood Solutions, LLC on behalf of the shop named below</p>
        <div>Shop / business name</div>
        <div style={formLine} />
        <div>Customer name</div>
        <div style={formLine} />
        <div>Mobile phone number *</div>
        <div style={formLine} />
        <p><strong>SMS consent:</strong> By signing below I agree to receive SMS text messages from this shop. Messages are transactional: invoice, estimate, or Pay now links. This is not a marketing list. Consent is not required to make a purchase.</p>
        <p><strong>Frequency:</strong> Typically 1 to 4 messages per invoice or estimate cycle.</p>
        <p><strong>Rates:</strong> Message and data rates may apply depending on your mobile plan.</p>
        <p><strong>Help and stop:</strong> Reply HELP for help or STOP to cancel at any time.</p>
        <p><strong>Terms and privacy:</strong> busmgr.com/legal/terms and busmgr.com/legal/privacy</p>
        <p>[ ] I agree to receive SMS text messages from this business as described above. I understand that consent is not required to make a purchase.</p>
        <div>Customer signature *</div>
        <div style={formLine} />
        <div>Date *</div>
        <div style={formLine} />
        <p style={{ fontSize: '0.9rem', color: '#4b5563' }}>For office use only: Form completed on ________ by staff member ________</p>
      </div>
    </>
  );
}

export default function LegalPageView() {
  const { pathname } = useLocation();
  let body = <PrivacyBody />;
  if (pathname === '/legal/terms') body = <TermsBody />;
  if (pathname === '/legal/sms-opt-in') body = <SmsOptInBody />;
  if (pathname === '/legal/data-deletion') body = <DataDeletionBody />;
  return <LegalChrome>{body}</LegalChrome>;
}
